import { useEffect, useRef, useState } from 'react';

const SILENCE_THRESHOLD = 0.06;
const SILENCE_DURATION_MS = 10000; // 10 seconds

interface UseSilenceDetectionProps {
  volumeLevel: number;
  isRecording: boolean;
}

interface UseSilenceDetectionReturn {
  showSilenceWarning: boolean;
  dismissWarning: () => void;
}

export const useSilenceDetection = ({
  volumeLevel,
  isRecording,
}: UseSilenceDetectionProps): UseSilenceDetectionReturn => {
  const [showSilenceWarning, setShowSilenceWarning] = useState(false);
  const silenceStartTimeRef = useRef<number | null>(null);
  const lastDismissalTimeRef = useRef<number | null>(null);
  const volumeLevelRef = useRef<number>(volumeLevel);

  volumeLevelRef.current = volumeLevel;

  useEffect(() => {
    if (!isRecording) {
      silenceStartTimeRef.current = null;
      setShowSilenceWarning(false);
      return;
    }

    const intervalId = setInterval(() => {
      const now = Date.now();
      const isSilent = volumeLevelRef.current < SILENCE_THRESHOLD;

      if (isSilent) {
        if (silenceStartTimeRef.current === null) {
          silenceStartTimeRef.current = now;
        } else {
          const silenceDuration = now - silenceStartTimeRef.current;

          if (silenceDuration >= SILENCE_DURATION_MS) {
            const timeSinceLastDismissal = lastDismissalTimeRef.current
              ? now - lastDismissalTimeRef.current
              : Infinity;

            if (timeSinceLastDismissal >= SILENCE_DURATION_MS) {
              setShowSilenceWarning(true);
            }
          }
        }
      } else {
        silenceStartTimeRef.current = null;
        setShowSilenceWarning(false);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
      silenceStartTimeRef.current = null;
    };
  }, [isRecording]);

  const dismissWarning = () => {
    setShowSilenceWarning(false);
    lastDismissalTimeRef.current = Date.now();
  };

  return {
    showSilenceWarning,
    dismissWarning,
  };
};
